import React from 'react';
import type {
  AutocompleteChangeReason,
  AutocompleteFreeSoloValueMapping,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  AutocompleteValue,
  FilterOptionsState,
  InputProps,
} from '@mui/material';
import { Autocomplete as MuiAutocomplete, createFilterOptions } from '@mui/material';
import { isNil, noop } from 'lodash';

import type { TextFieldProps } from '~/components/core/Molecules/Fields/TextField';
import TextField from '~/components/core/Molecules/Fields/TextField';
import { useAutoSelectSingleOption } from '~/hooks/useAutoSelectSingleOption';
import { isLocalEnv, reportErrorInProductionOrThrow } from '~/Utils';

export interface AutoCompleteProps<T, FreeSolo extends boolean | undefined = undefined> {
  id: string;
  label?: string;
  options: T[];
  value?: AutocompleteValue<T, undefined, undefined, FreeSolo>;
  /**
   * Declared `=> string`, but do not assume it: most consumers are `.jsx`, and `checkJs` is off, so
   * TS never sees them. A dictionary lookup that misses returns `undefined` at runtime — which is
   * why the display read below coerces. Kept narrow on purpose: widening to `string | undefined`
   * would make the honestly-typed `string | undefined` call site assignable too, removing the one
   * case TS does still catch here.
   */
  getOptionLabel: (option: T) => string;
  textFieldProps?: TextFieldProps<T>;
  showOnly?: boolean;
  disabled?: boolean;
  sortAlphabetic?: boolean;
  /**
   * Parameters are deliberately wider than MUI's `onChange`: the wrapper itself also invokes the
   * handler (auto-select-single-option) with no event and its own reason, so the event may be
   * `undefined` and the reason includes `'auto-select-single-option'` alongside MUI's union.
   * Being wider is also what lets the handler forward to v5 without a cast.
   */
  onChange?: (
    event: React.SyntheticEvent | undefined,
    value?: AutocompleteValue<T, undefined, undefined, FreeSolo>,
    reason?: AutocompleteChangeReason | 'auto-select-single-option'
  ) => void;
  autoSelect?: boolean;
  // When the options contain exactly one option, select it automatically. On by default.
  autoSelectSingleOption?: boolean;
  autoComplete?: boolean;
  autoHighlight?: boolean;
  /**
   * `event` is `null` on the `'reset'` path: MUI calls `resetInputValue(null, value)` whenever the
   * controlled value changes, which reaches `onInputChange(null, …, 'reset')`. MUI's own d.ts types
   * the parameter non-null, so this is deliberately wider than what it declares.
   */
  onInputChange?: (event: React.SyntheticEvent | null, value: string, reason: AutocompleteInputChangeReason) => void;
  className?: string;
  InputProps?: Partial<InputProps>;
  clearOnBlur?: boolean;
  freeSolo?: FreeSolo;
  /** v4's name for `isOptionEqualToValue`; remapped below so the four call sites keep working. */
  getOptionSelected?: (option: T, value: T) => boolean;
  getOptionDisabled?: (option: T) => boolean;
  /** Keys each option row; MUI falls back to the label, which collides when two options share one. */
  getOptionKey?: (option: T) => string;
  /**
   * Renders one option's content. Takes the option and state, and returns content without an
   * `<li>` of its own — the wrapper adapts that to v5's `(props, option, state)` signature below.
   * No consumer reads `state` today; it is here because the v4 contract exposed it.
   */
  renderOption?: (option: T, state: AutocompleteRenderOptionState) => React.ReactNode;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  loading?: boolean;
  groupBy?: (options: T) => string;
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  classes?: { [key: string]: string };
}

const AutoComplete = <T, FreeSolo extends boolean | undefined = undefined>({
  className,
  id,
  options,
  getOptionLabel,
  onChange = noop,
  value,
  disabled,
  autoSelect,
  autoSelectSingleOption = true,
  autoHighlight,
  autoComplete,
  onInputChange = noop,
  label,
  textFieldProps,
  InputProps,
  clearOnBlur,
  freeSolo,
  getOptionSelected,
  getOptionDisabled,
  getOptionKey,
  renderOption,
  renderInput,
  loading,
  groupBy,
  filterOptions,
  classes,
  ...rest
}: AutoCompleteProps<T, FreeSolo>): JSX.Element => {
  if (Object.keys(rest).length > 0 && isLocalEnv()) {
    reportErrorInProductionOrThrow(
      `Additional unimplemented props was passed to AutoComplete component ${Object.keys(rest)}`,
      true
    );
  }

  // A `getOptionLabel` that cannot label the current value is normal here: several call sites start
  // from a sentinel like `''` and only map real options. v4 rendered nothing in that case; v5 puts
  // the label straight into the input, so the Policy Country filter opened showing the literal text
  // "undefined". Coerce for display only -- the callers below deliberately keep using the raw
  // `getOptionLabel`, because they distinguish "no label" from "empty label" and `''` is a
  // distinct answer that changes what they decide. MUI types the argument as `T | string` to cover
  // `freeSolo` input; it has always passed that string through at runtime, so the cast changes nothing.
  const getOptionLabelForDisplay = (option: T | AutocompleteFreeSoloValueMapping<FreeSolo>): string => {
    const label = getOptionLabel(option as T);
    return typeof label === 'string' ? label : '';
  };

  const selectableOptions = getOptionDisabled ? options.filter((option) => !getOptionDisabled(option)) : options;
  useAutoSelectSingleOption<T>({
    enabled: autoSelectSingleOption && !disabled && !loading,
    // Raw `getOptionLabel`, deliberately not the display coercion above. A label goes missing for
    // two reasons this cannot tell apart: a sentinel that was never an option (the country filter
    // starts at `''`), or a real value whose lookup misses because the dict behind it is still
    // loading or the option was removed (`documentTypesDict`). Coercing both to `''` would report
    // the second as an empty field, and a momentarily-single option would auto-select over a real
    // value. Reporting "not empty" only costs an auto-fill that the user can still make by hand.
    isEmpty: isNil(value) || getOptionLabel(value as T) === '',
    options: selectableOptions,
    onSelect: (option) => onChange(undefined, option, 'auto-select-single-option'),
  });

  return (
    <MuiAutocomplete
      className={className}
      id={id}
      options={options}
      getOptionLabel={getOptionLabelForDisplay}
      onChange={onChange}
      filterOptions={
        filterOptions
          ? filterOptions
          : (_, value) =>
              // Also raw: the `?.` short-circuits an unlabelable option to `undefined`, which drops
              // it from the list. Coercing to `''` would instead surface it as a blank row whenever
              // the search box is empty, since `''.includes('')` is true.
              options.filter((option) =>
                getOptionLabel(option)?.toLowerCase()?.includes(value.inputValue.toLowerCase())
              )
      }
      value={value}
      disabled={disabled}
      autoSelect={autoSelect}
      autoHighlight={autoHighlight}
      autoComplete={autoComplete}
      onInputChange={onInputChange}
      clearOnBlur={clearOnBlur}
      freeSolo={freeSolo}
      isOptionEqualToValue={getOptionSelected}
      getOptionDisabled={getOptionDisabled}
      // MUI only keys rendered options, which are always `T`; the `T | string` it declares covers
      // `freeSolo` input that never becomes a row, so the cast changes nothing at runtime.
      getOptionKey={
        getOptionKey ? (option: T | AutocompleteFreeSoloValueMapping<FreeSolo>) => getOptionKey(option as T) : undefined
      }
      // v4 called `renderOption(option, state)` and wrapped whatever came back in its own `<li>`.
      // v5 calls `renderOption(props, option, state)` and hands the `<li>` props to the caller to
      // spread. Both differences are absorbed here so the call sites stay on the v4 shape.
      renderOption={
        renderOption
          ? (optionProps, option, state) => <li {...optionProps}>{renderOption(option, state)}</li>
          : undefined
      }
      renderInput={(params) => {
        // TextField is not supporting size param
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { size, ...restParams } = params;

        return renderInput ? (
          <>{renderInput(params)}</>
        ) : (
          <TextField
            className="my-4"
            label={label}
            {...restParams}
            {...textFieldProps}
            InputProps={{
              ...InputProps,
              ...params.InputProps,
              margin: 'dense', // to match other TextField components
            }}
          />
        );
      }}
      loading={loading}
      groupBy={groupBy}
      classes={classes}
    />
  );
};

export default AutoComplete;

// Re-exported so a consumer that tunes the wrapper's `filterOptions` gets MUI's helper from the
// same module it gets the component from, rather than reaching past the wrapper into `@mui/material`
// (which the MUI import ratchet bans outside components/core).
export { createFilterOptions };
